/* FamilyTab — simple, clean admin view of the family around this memorial.
   No illustrated tree, no circles, no decorative branches. Just two
   sections (Immediate family / Extended family) rendered as clickable
   rows with the essentials. An inline "Add someone" form sits at the top. */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError, BACKEND_URL } from '../../lib/api';
import { RelationshipSelector, resolveRelationship, isRelationshipMissing } from '../../components/RelationshipSelector';
import { FamilyTreeView, TREE_POSITIONS } from '../../components/FamilyTreeView';
import { FamilyMemberPanel } from '../../components/FamilyMemberPanel';

// Immediate-family relationships. Everything else is "extended".
const IMMEDIATE = new Set([
  'spouse', 'partner',
  'husband', 'wife',
  'son', 'daughter', 'child',
  'father', 'mother', 'parent',
  'brother', 'sister', 'sibling',
]);

function isImmediate(rel) {
  return IMMEDIATE.has((rel || '').trim().toLowerCase());
}

const EMPTY_FORM = {
  name: '', relationship: '', relationship_other: '',
  birth_date: '', death_date: '',
  is_living: false, location: '', resting_place: '', notes: '',
};

export function FamilyTab({ memorialId, memorial }) {
  const [family, setFamily] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [expandedId, setExpandedId] = useState(null);
  // iter97.10 — Person detail modal (clickable tree leaves).
  const [activeMember, setActiveMember] = useState(null);

  const load = useCallback(
    () => api.get(`/memorials/${memorialId}/family`).then(({ data }) => setFamily(data.family)),
    [memorialId],
  );
  useEffect(() => { load(); }, [load]);

  const { immediate, extended } = useMemo(() => {
    const imm = [];
    const ext = [];
    for (const f of family) (isImmediate(f.relationship) ? imm : ext).push(f);
    return { immediate: imm, extended: ext };
  }, [family]);

  const updateTreePosition = async (fid, position) => {
    try {
      await api.patch(`/family/${fid}/tree-position`, { position });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const add = async () => {
    if (!form.name) {
      toast.error('Please add a name.');
      return;
    }
    if (isRelationshipMissing(form.relationship, form.relationship_other)) {
      toast.error('Please choose a relationship (or type one when "Other" is selected).');
      return;
    }
    try {
      const payload = { ...form, relationship: resolveRelationship(form.relationship, form.relationship_other) };
      delete payload.relationship_other;
      if (payload.is_living) {
        payload.death_date = null;
        payload.resting_place = null;
      } else if (!payload.death_date) {
        payload.is_living = true;
        payload.resting_place = null;
      }
      await api.post(`/memorials/${memorialId}/family`, payload);
      setForm(EMPTY_FORM);
      toast.success('Person added — ready for the next one');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (fid) => {
    if (!window.confirm('Remove this relative?')) return;
    await api.delete(`/family/${fid}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="family-tab">
      <FamilyMemberPanel
        member={activeMember}
        open={!!activeMember}
        onClose={() => setActiveMember(null)}
        canCreate={true}
        canEdit={true}
        onChanged={async () => {
          const { data } = await api.get(`/memorials/${memorialId}/family`);
          setFamily(data.family);
          if (activeMember) {
            const fresh = (data.family || []).find((f) => f.family_id === activeMember.family_id);
            setActiveMember(fresh || null);
          }
        }}
        sourceMemorialId={memorialId}
        sourceMemorialName={memorial?.full_name}
      />

      {/* ---- Auto-built family tree ---- */}
      {family.length > 0 && (
        <div className="ml-card overflow-hidden" data-testid="family-tree-card">
          <div className="flex items-baseline justify-between gap-4 mb-5 flex-wrap">
            <div>
              <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Family tree</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Auto-built from the people you add below. Use the &quot;Place as&quot; picker in each card to fine-tune position.
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.get(`/memorials/${memorialId}/family-tree.pdf`, { responseType: 'blob' });
                  const url = URL.createObjectURL(data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `family-tree-${memorialId}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Family Tree PDF downloaded.');
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="ml-btn-secondary text-xs px-4 py-2"
              data-testid="family-tree-pdf-btn"
            >
              Download Family Tree PDF
            </button>
          </div>
          <FamilyTreeView
            deceasedName={memorial?.full_name || 'This memorial'}
            deceasedPhotoUrl={
              memorial?.profile_photo_url
                ? (memorial.profile_photo_url.startsWith('http')
                    ? memorial.profile_photo_url
                    : `${BACKEND_URL}${memorial.profile_photo_url}`)
                : null
            }
            deceasedBirthYear={memorial?.date_of_birth ? memorial.date_of_birth.slice(0, 4) : null}
            deceasedDeathYear={memorial?.date_of_passing ? memorial.date_of_passing.slice(0, 4) : null}
            members={family}
            onMemberClick={(m) => setActiveMember(m)}
            theme="light"
          />
        </div>
      )}

      {/* ---- Add someone ---- */}
      <div className="ml-card">
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Family</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Add anyone — immediate family, cousins, in-laws, close friends. Status (with us today / in memory) is set per person.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <input
            className="ml-input" placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="fam-name"
          />
          <div>
            <RelationshipSelector
              preset="family"
              value={form.relationship}
              customValue={form.relationship_other}
              onChange={(rel, other) => setForm({ ...form, relationship: rel, relationship_other: other })}
              placeholder="Relationship…"
              selectTestId="fam-rel"
              inputTestId="fam-rel-other"
            />
          </div>
          <input
            type="date" className="ml-input" placeholder="Born"
            value={form.birth_date}
            onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
          />
          <div className="flex items-center gap-3">
            <input
              type="date" className="ml-input flex-1" placeholder="Date of passing"
              value={form.death_date}
              disabled={form.is_living}
              onChange={(e) => setForm({ ...form, death_date: e.target.value })}
              data-testid="fam-death"
            />
            <label className="inline-flex items-center gap-2 cursor-pointer select-none text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.75)] whitespace-nowrap">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[hsl(var(--secondary))]"
                checked={form.is_living}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_living: e.target.checked,
                    death_date: e.target.checked ? '' : form.death_date,
                    resting_place: e.target.checked ? '' : form.resting_place,
                  })
                }
                data-testid="fam-living"
              />
              Still living
            </label>
          </div>
          <input
            className="ml-input sm:col-span-2"
            placeholder={form.is_living || !form.death_date ? 'Location (where they live)' : 'Last known location (optional)'}
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="fam-location"
          />
          {!form.is_living && form.death_date && (
            <input
              className="ml-input sm:col-span-2"
              placeholder="Resting place / cemetery"
              value={form.resting_place}
              onChange={(e) => setForm({ ...form, resting_place: e.target.value })}
              data-testid="fam-resting-place"
            />
          )}
          <textarea
            rows={2} className="ml-input sm:col-span-2" placeholder="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <button onClick={add} className="ml-btn-outline mt-3" data-testid="fam-add-btn">
          <UserPlus className="w-4 h-4" /> Add to family
        </button>
      </div>

      {/* ---- Immediate family ---- */}
      <FamilyGroup
        title="Immediate family"
        subtitle="Spouses, children, parents, and siblings."
        people={immediate}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        onRemove={remove}
        onUpdateTreePosition={updateTreePosition}
        onEdit={setActiveMember}
        testid="family-section-immediate"
      />

      {/* ---- Extended family ---- */}
      <FamilyGroup
        title="Extended family"
        subtitle="Grandparents, cousins, in-laws, dear friends."
        people={extended}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        onRemove={remove}
        onUpdateTreePosition={updateTreePosition}
        onEdit={setActiveMember}
        testid="family-section-extended"
      />

      {family.length === 0 && (
        <div className="ml-card text-center py-14">
          <Users className="w-6 h-6 text-[hsl(var(--primary)/0.35)] mx-auto" strokeWidth={1.4} />
          <div className="ml-overline mt-4">Nothing here yet</div>
          <p className="font-serif italic text-[hsl(var(--primary)/0.7)] mt-3">
            Add the first relative above to begin.
          </p>
        </div>
      )}
    </div>
  );
}

function FamilyGroup({ title, subtitle, people, expandedId, setExpandedId, onRemove, onUpdateTreePosition, onEdit, testid }) {
  if (!people.length) return null;
  return (
    <div className="ml-card" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">{title}</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{subtitle}</p>
        </div>
        <span className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)]">
          {people.length} {people.length === 1 ? 'person' : 'people'}
        </span>
      </div>

      <ul className="mt-5 divide-y divide-[hsl(var(--primary)/0.08)] border-y border-[hsl(var(--primary)/0.08)]">
        {people.map((f) => {
          const living = (typeof f.is_living === 'boolean') ? f.is_living : !f.death_date;
          const open = expandedId === f.family_id;
          const initial = (f.name || '?').trim().charAt(0).toUpperCase();
          return (
            <li key={f.family_id} data-testid={`family-row-${f.family_id}`}>
              <button
                type="button"
                onClick={() => setExpandedId(open ? null : f.family_id)}
                className="w-full py-4 flex items-center gap-4 text-left hover:bg-[hsl(var(--primary)/0.04)] transition-colors px-2 rounded-sm"
                aria-expanded={open}
                data-testid={`family-row-toggle-${f.family_id}`}
              >
                <div className="w-11 h-11 rounded-full bg-[hsl(var(--muted))] border border-[hsl(var(--primary)/0.12)] flex items-center justify-center font-serif text-lg text-[hsl(var(--primary)/0.75)] shrink-0 overflow-hidden">
                  {f.photo_url
                    ? <img src={f.photo_url} alt={f.name} className="w-full h-full object-cover" />
                    : initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[hsl(var(--primary))] truncate">{f.name}</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))] mt-0.5">
                    {f.relationship}
                    <span className="mx-2 text-[hsl(var(--primary)/0.25)]">·</span>
                    {living ? 'With Us Today' : 'In memory'}
                    {f.location && (
                      <span className="normal-case tracking-normal text-[hsl(var(--primary)/0.55)] ml-2">
                        · {f.location}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${living ? 'bg-[hsl(var(--secondary))]' : 'bg-[hsl(var(--accent))]'}`}
                  aria-hidden="true"
                />
              </button>
              {open && (
                <div className="px-2 pb-5 -mt-1 text-sm text-[hsl(var(--primary)/0.78)] space-y-1.5 font-light">
                  {f.birth_date && (
                    <div><span className="ml-overline !text-[hsl(var(--primary)/0.45)] !text-[10px]">Born</span><span className="ml-2">{f.birth_date}</span></div>
                  )}
                  {!living && f.death_date && (
                    <div><span className="ml-overline !text-[hsl(var(--primary)/0.45)] !text-[10px]">Passed</span><span className="ml-2">{f.death_date}</span></div>
                  )}
                  {f.resting_place && (
                    <div><span className="ml-overline !text-[hsl(var(--primary)/0.45)] !text-[10px]">Resting place</span><span className="ml-2">{f.resting_place}</span></div>
                  )}
                  {f.notes && <p className="pt-1 leading-relaxed italic font-serif text-[hsl(var(--primary)/0.72)]">{f.notes}</p>}
                  <div className="pt-3 flex flex-wrap items-center gap-3">
                    <label className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)] inline-flex items-center gap-2">
                      Place as
                      <select
                        className="ml-input !py-1.5 !text-xs normal-case tracking-normal"
                        value={f.tree_position || 'auto'}
                        onChange={(e) => onUpdateTreePosition(f.family_id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`family-tree-position-${f.family_id}`}
                      >
                        {TREE_POSITIONS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(f); }}
                      className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary))] hover:underline"
                      data-testid={`family-edit-btn-${f.family_id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onRemove(f.family_id); }}
                      className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--destructive))] hover:underline ml-auto"
                      data-testid={`family-delete-${f.family_id}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
